import { DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import Form from "@saleor/components/Form";
import NotFoundPage from "@saleor/components/NotFoundPage";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { customerUrl } from "@saleor/customers/urls";
import { useDropOffListQuery } from "@saleor/dropOff/queries";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { findOrderEnumNames } from "@saleor/intl";
import { getStringOrPlaceholder, maybe } from "@saleor/misc";
import OrderAssignRiderDialog from "@saleor/motherOrders/components/OrderAssignRiderDialog";
import OrderCancelDialog from "@saleor/motherOrders/components/OrderCancelDialog";
import OrderConfirmDialog from "@saleor/motherOrders/components/OrderConfirmationDialog";
import OrderDetailsPage from "@saleor/motherOrders/components/OrderDetailsPage";
import RiderRefreshDialog from "@saleor/motherOrders/components/OrderDetailsPage/components/RiderRefreshDialog";
import OrderGenerateShippingLabel from "@saleor/motherOrders/components/OrderGenerateShippingLabel";
import OrderMarkAsCompletedDialog from "@saleor/motherOrders/components/OrderMarkAsCompletedDialog";
import OrderProductDetailsDialog from "@saleor/motherOrders/components/OrderProductDetailsDialog";
import { OrderCancel } from "@saleor/motherOrders/types/OrderCancel";
import { OrderUrlDialog, OrderUrlQueryParams } from "@saleor/motherOrders/urls";
import { OrderCourierEnum } from "@saleor/motherOrders/utils/enums";
import { LAST_RIDER_ASSIGNED_AT } from "@saleor/motherOrders/utils/handlers";
import { productUrl } from "@saleor/products/urls";
import {
  LockStatus,
  LogisticsStatusEnum,
  SmopOrderStatusEnum,
  SmopUserTypeCode,
  TemplateStatusEnum
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import {
  canManageAllMalls,
  canManageFulfillment
} from "@saleor/utils/handlers/userHandler";
import { generatePdf } from "@saleor/utils/pdf/handler";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  TypedOrderCancelMutation,
  useBook2Go,
  useBookThirdPartyRider,
  useMarkAsCompletedMutation,
  useMarkAsPackedMutation,
  useMarkAsReadyToShipMutation,
  useMarkAsShippedMutation,
  useOrderAssignHomeLocation,
  useOrderAssignRider,
  useOrderCancelGrabExpress,
  useOrderCancelPandagoAuto,
  useOrderMarkAsValid,
  useUpdateOrderStatusWithOverride,
  useUpdateShippingLabel
} from "../../mutations";
import { TypedOrderDetailsQuery, useSearchRider } from "../../queries";

interface OrderDetailsProps {
  id: string;
  params: OrderUrlQueryParams;
  backUrlRedirect: string;
  detailsUrl(id: string, params?: OrderUrlQueryParams): string;
}

export const OrderDetails: React.FC<OrderDetailsProps> = props => {
  const { id, params, backUrlRedirect, detailsUrl } = props;

  const intl = useIntl();
  const notify = useNotifier();
  const navigate = useNavigator();
  const { user } = useUser();

  const disableAction = !canManageFulfillment();
  const enableManageMallActions = canManageAllMalls();

  const [allReceivedAtFc, setAllReceivedAtFc] = useState(false);
  const [allValidForPacking, setAllValidForPacking] = useState(false);

  const [generateShippingLabel, setGenerateShippingLabel] = useState(false);
  const [orderMallId, setOrderMallId] = React.useState("");

  const [serverError, setServerError] = useState({
    timeDelivered: null,
    timeDispatched: null
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    OrderUrlDialog,
    OrderUrlQueryParams
  >(navigate, params => detailsUrl(id, params), params);

  const handleBack = () => navigate(backUrlRedirect);

  const onSubmitCancelOrder = (data: OrderCancel) => {
    if (!maybe(() => data.orderCancel.errors.length !== 0)) {
      notify({
        status: "success",
        text:
          "Order " + data.orderCancel.order.orderCode + " has been cancelled."
      });
    } else {
      notify({
        status: "error",
        text: "Something went wrong"
      });
    }
    navigate(detailsUrl(id));
  };

  const onError = errors => {
    const [error] = errors.graphQLErrors;
    notify({
      status: "error",
      text: error?.message
    });
  };

  const {
    search: searchRider,
    loadMore: loadMoreRider,
    result: resultRider
  } = useSearchRider({
    skip: !orderMallId,
    variables: {
      first: 100,
      malls: [orderMallId],
      query: "",
      status: LockStatus.ACTIVE,
      userType: [SmopUserTypeCode.RIDER]
    }
  });

  return (
    <TypedOrderDetailsQuery displayLoader variables={{ getTimeout: true, id }}>
      {({ data, refetch }) => {
        const order = data?.order;
        const checkSubOrder = data?.order && data?.order.subOrders;

        const [orderId, setOrderId] = React.useState("");

        setOrderMallId(order?.mall?.id);

        React.useEffect(() => {
          if (params.orderId) {
            setOrderId(params.orderId);
          }
        }, [params.orderId]);

        if (checkSubOrder) {
          const subOrderIndex = checkSubOrder.length - 1;
          const allSubOrderStat = [];
          const allSubValidForPack = [];

          for (let o = 0; o <= subOrderIndex; o++) {
            if (
              checkSubOrder[o].smopStatus === "RECEIVED_AT_FC" ||
              checkSubOrder[o].smopStatus === "CANCELED"
            ) {
              allSubOrderStat.push(true);
              if (checkSubOrder[o].smopStatus === "CANCELED") {
                allSubValidForPack.push(true);
              } else {
                if (checkSubOrder[o].validatedForPackingBy) {
                  allSubValidForPack.push(true);
                } else {
                  allSubValidForPack.push(false);
                }
              }
            } else {
              allSubOrderStat.push(false);
            }
          }
          if (allSubOrderStat.includes(false)) {
            setAllReceivedAtFc(false);
          } else {
            setAllReceivedAtFc(true);
          }

          if (allSubValidForPack.includes(false)) {
            setAllValidForPacking(false);
          } else {
            setAllValidForPacking(true);
          }
        }

        const [orderMarkAsValid] = useOrderMarkAsValid({
          onCompleted: data => {
            if (data.storeOrderMarkAsValidForPacking.orderErrors.length === 0) {
              notify({
                status: "success",
                text: intl.formatMessage({
                  defaultMessage: `Scanned Parcel ID matched the associated Parcel ID by the Store.`
                })
              });
            } else {
              notify({
                status: "error",
                text: intl.formatMessage({
                  defaultMessage: `Something Went Wrong`
                })
              });
            }
            navigate(detailsUrl(id));
            refetch();
          },
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [
          orderMarkAsPacked,
          orderMarkAsPackedOpts
        ] = useMarkAsPackedMutation({
          onCompleted: data => {
            if (data) {
              if (generateShippingLabel === true) {
                generatePdf(
                  { order: data.orderMarkAsPacked.order },
                  "/generate-nationwide-shipping-label",
                  true,
                  "Nationwide-Shipping-Label" + order.orderCode + ".pdf"
                );
              }
              notify({
                status: "success",
                text:
                  "Order " +
                  data.orderMarkAsPacked.order.orderCode +
                  " has been packed."
              });
            } else {
              notify({
                status: "error",
                text: intl.formatMessage({
                  defaultMessage: `Something Went Wrong`
                })
              });
            }
            navigate(detailsUrl(id));
            refetch();
          }
        });

        const [
          orderMarkAsReadyToShip,
          orderMarkAsReadyToShipOpts
        ] = useMarkAsReadyToShipMutation({
          onCompleted: data => {
            if (data) {
              notify({
                status: "success",
                text:
                  "Order " +
                  data.orderMarkAsReadyToShip.order.orderCode +
                  " is now ready to ship."
              });
            } else {
              notify({
                status: "error",
                text: intl.formatMessage({
                  defaultMessage: `Something Went Wrong`
                })
              });
            }
            navigate(detailsUrl(id));
            refetch();
          }
        });

        const [
          orderMarkAsShipped,
          orderMarkAsShippedOpts
        ] = useMarkAsShippedMutation({
          onCompleted: data => {
            if (data.orderMarkAsShipped.errors.length === 0) {
              notify({
                status: "success",
                text: `Order ${data.orderMarkAsShipped.order.orderCode} is now marked as shipped.`
              });
              navigate(detailsUrl(id));
            } else {
              if (
                data.orderMarkAsShipped.errors[0].field === "timeDispatched"
              ) {
                setServerError({
                  ...serverError,
                  timeDispatched: data.orderMarkAsShipped.errors[0].message
                });

                // open assign rider dialog
                openModal("onClickAssignRider", {
                  orderId: id
                });
              } else {
                notify({
                  status: "error",
                  text: data.orderMarkAsShipped.errors[0].message
                });
                navigate(detailsUrl(id));
              }
            }

            refetch();
          }
        });

        const [
          orderMarkAsCompleted,
          orderMarkAsCompletedOpts
        ] = useMarkAsCompletedMutation({
          onCompleted: data => {
            if (data.orderMarkAsCompleted.errors.length === 0) {
              notify({
                status: "success",
                text: `Order ${data.orderMarkAsCompleted.order.orderCode} has been completed.`
              });
              navigate(detailsUrl(id));
            } else {
              if (
                data.orderMarkAsCompleted.errors[0].field === "timeDelivered"
              ) {
                setServerError({
                  ...serverError,
                  timeDelivered: data.orderMarkAsCompleted.errors[0].message
                });

                // open assign rider dialog
                openModal("onClickAssignRider", {
                  orderId: id
                });
              } else {
                notify({
                  status: "error",
                  text: data.orderMarkAsCompleted.errors[0].message
                });
                navigate(detailsUrl(id));
              }
            }

            refetch();
          },
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [assignRider] = useOrderAssignRider({
          onCompleted: data => {
            const specifiedErrors = data.orderAssignRider.orderErrors;
            const hasCooldownError = specifiedErrors.some(
              error => error.field === LAST_RIDER_ASSIGNED_AT
            );
            if (hasCooldownError) {
              setCooldownDialogOpen(true);
              return;
            } else if (specifiedErrors.length === 0) {
              if (
                data.orderAssignRider.order.thirdPartyRider?.deliveryCourier ===
                  null ||
                data.orderAssignRider.order?.thirdPartyRider === null
              ) {
                notify({
                  status: "success",
                  text: `Order ${data.orderAssignRider.order.orderCode} has been assigned a rider`
                });
              } else {
                notify({
                  status: "success",
                  text: `Order ${order.orderCode} has been assigned a rider`
                });
              }
            } else {
              // Spiel when the rider exceeded their COD limit
              notify({
                status: "error",
                text: `${data.orderAssignRider.orderErrors[0].message}`
              });
            }
            navigate(detailsUrl(id));
            refetch();
          },
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [updateShippingLabel] = useUpdateShippingLabel({
          onCompleted: data => {
            if (data.orderUpdateShippingLabel.orderErrors.length === 0) {
              notify({
                status: "success",
                text: `Shipping Label has been updated`
              });
            } else {
              notify({
                status: "error",
                text: `${data.orderUpdateShippingLabel.orderErrors[0].message}`
              });
            }
            navigate(detailsUrl(id));
            refetch();
          },
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [orderUpdateStatus] = useUpdateOrderStatusWithOverride({
          onCompleted: data => {
            if (data.updateOrderStatus.orderErrors.length === 0) {
              notify({
                status: "success",
                text: `Order ${
                  data.updateOrderStatus.order.orderCode
                } is now marked as ${findOrderEnumNames(
                  data.updateOrderStatus.order.smopStatus
                )}.`
              });
            } else {
              notify({
                status: "error",
                text: `${data.updateOrderStatus.orderErrors[0].message}`
              });
            }
            refetch();
          },
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const onSubmitMarkAsValid = (storeOrderId, parcelNo) => {
          orderMarkAsValid({
            variables: {
              id: storeOrderId,
              parcelNo
            }
          });
        };

        const onSubmitStatus = (type, isGenerateShippingLabel = false) => {
          switch (type) {
            case "PACKED":
              if (isGenerateShippingLabel === true) {
                setGenerateShippingLabel(true);
              }
              orderMarkAsPacked({
                variables: {
                  id
                }
              });
              break;
            case "READY_TO_SHIP":
              orderMarkAsReadyToShip({
                variables: {
                  id
                }
              });
              break;
            case "SHIPPED":
              orderMarkAsShipped({
                variables: {
                  id
                }
              });
              break;
          }
        };

        const updateStatusMutation = newStatus =>
          orderUpdateStatus({
            variables: {
              id,
              smopStatus: newStatus
            }
          });

        const onCompletedConfirm = reason => {
          orderMarkAsCompleted({
            variables: {
              id,
              reason
            }
          });
        };

        const onUpdateShippingLabel = (
          courier,
          awbNo,
          dispatchNo,
          specialNotes
        ) => {
          updateShippingLabel({
            variables: {
              awbNo,
              courier,
              dispatchNo,
              id,
              specialNotes
            }
          });
        };

        if (order === null) {
          return <NotFoundPage onBack={handleBack} />;
        }

        const [orderCancelGrabExpress] = useOrderCancelGrabExpress({
          onCompleted: data => {
            if (data.orderCancelGrabExpress.orderErrors.length === 0) {
              notify({
                status: "success",
                text: `You have successfully cancelled this booking.`
              });
            } else {
              notify({
                status: "error",
                text: `Unable to cancel. Please try again.`
              });
            }
          }
        });

        const [orderCancelPandagoAuto] = useOrderCancelPandagoAuto({
          onCompleted: data => {
            if (data?.orderCancelPandagoAuto?.orderErrors?.length === 0) {
              notify({
                status: "success",
                text: `You have successfully cancelled this booking.`
              });
            } else {
              notify({
                status: "error",
                text: `Unable to cancel. Please try again.`
              });
            }
          }
        });

        const [cooldownDialogOpen, setCooldownDialogOpen] = useState(false);
        const [bookThirdPartyRider, bookingResult] = useBookThirdPartyRider({
          onCompleted: ({ bookThirdPartyRider }) => {
            const orderErrors = bookThirdPartyRider?.orderErrors?.length === 0;
            const specifiedErrors = bookThirdPartyRider?.orderErrors;
            const hasCooldownError = specifiedErrors.some(
              error => error.field === LAST_RIDER_ASSIGNED_AT
            );

            const courier =
              bookThirdPartyRider?.order?.thirdPartyRider?.deliveryCourier
                ?.order?.courier;
            const referenceNo =
              bookThirdPartyRider.order?.thirdPartyRider?.deliveryCourier
                ?.referenceNo;
            const onBookingCourier =
              courier === OrderCourierEnum.GRAB_EXPRESS
                ? "Grab Express ID"
                : courier === OrderCourierEnum.PANDAGO_AUTO
                ? "Pandago Auto ID"
                : "";
            if (hasCooldownError) {
              setCooldownDialogOpen(true);
              return;
            } else if (orderErrors) {
              notify({
                status: "success",
                text: `You have successfully booked with ${onBookingCourier} ${referenceNo}`
              });
              navigate(detailsUrl(id));
              refetch();
            } else {
              notify({
                status: "error",
                text: `${bookThirdPartyRider?.orderErrors[0]?.message}`
              });
            }
          },
          onError: errors => {
            const [error] = errors?.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [manualRebookThirdPartyRider] = useBook2Go({
          onCompleted: ({ book2go }) => {
            if (book2go?.orderErrors?.length === 0) {
              notify({
                status: "success",
                text: `You have successfully rebooked with airway bill of ${book2go.order.thirdPartyAwbCode}`
              });
              navigate(detailsUrl(id));
              refetch();
            } else {
              notify({
                status: "error",
                text: `${book2go?.orderErrors[0]?.message}`
              });
            }
          }
        });

        const { data: DropOffList } = useDropOffListQuery({
          displayLoader: true,
          skip: !order?.mall?.id,
          variables: {
            filter: {
              mall: order?.mall?.id,
              status: LogisticsStatusEnum.ACTIVE,
              templateStatus: TemplateStatusEnum.ACTIVE
            },
            first: 100
          }
        });

        const [
          assignHomeLocation,
          assignHomeLocationOpts
        ] = useOrderAssignHomeLocation({
          onCompleted: e => {
            if (e?.orderUpdateDropOffLocation?.orderErrors?.length === 0) {
              notify({
                status: "success",
                text: "Home Location Assigned!"
              });
            } else {
              notify({
                status: "error",
                text: `${e?.orderUpdateDropOffLocation?.orderErrors[0]?.message}`
              });
            }
            refetch();
            closeModal();
          },
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        return (
          <TypedOrderCancelMutation
            onCompleted={onSubmitCancelOrder}
            onError={onError}
          >
            {statusUpdate => {
              const UpdateStatus = async (cancelReasonData: any) => {
                const result = await statusUpdate({
                  variables: {
                    cancelReason: cancelReasonData,
                    id
                  }
                });
                return result.data.orderCancel.errors;
              };

              const onSubmitCancelOrder = (data: any) => {
                UpdateStatus(data.cancelReason);
              };

              return (
                <>
                  <RiderRefreshDialog
                    orderId={orderId}
                    open={cooldownDialogOpen}
                    onClose={() => {
                      setCooldownDialogOpen(false);
                      refetch();
                    }}
                  />
                  <WindowTitle
                    title={intl.formatMessage(
                      {
                        defaultMessage: "Order #{orderNumber}",
                        description: "window title"
                      },
                      {
                        orderNumber: getStringOrPlaceholder(data?.order?.number)
                      }
                    )}
                  />
                  <OrderDetailsPage
                    order={maybe(() => order, undefined)}
                    saveButtonBarState={
                      orderMarkAsReadyToShipOpts.status ||
                      orderMarkAsPackedOpts.status ||
                      orderMarkAsShippedOpts.status ||
                      orderMarkAsCompletedOpts.status
                    }
                    disabled={
                      orderMarkAsReadyToShipOpts.loading ||
                      orderMarkAsPackedOpts.loading ||
                      disableAction
                    }
                    ConfirmDialog={getStatus =>
                      openModal("onClickSaveButton", {
                        motherOrderStatus: getStatus
                      })
                    }
                    ConfirmCompleted={getStatus =>
                      openModal("onClickCompleted", {
                        motherOrderStatus: getStatus
                      })
                    }
                    motherOrderStatus={
                      data && data.order && data.order.smopStatus
                    }
                    onModal={action => openModal(action)}
                    onBack={handleBack}
                    shippingMethods={maybe(
                      () => data.order.availableShippingMethods,
                      []
                    )}
                    userPermissions={user?.userPermissions || []}
                    onOrderCancel={() => openModal("cancel")}
                    onFulfillmentCancel={fulfillmentId =>
                      navigate(
                        detailsUrl(id, {
                          action: "cancel-fulfillment",
                          id: fulfillmentId
                        })
                      )
                    }
                    onFulfillmentTrackingNumberUpdate={fulfillmentId =>
                      navigate(
                        detailsUrl(id, {
                          action: "edit-fulfillment",
                          id: fulfillmentId
                        })
                      )
                    }
                    onPaymentCapture={() => openModal("capture")}
                    onPaymentVoid={() => openModal("void")}
                    onPaymentRefund={() => openModal("refund")}
                    onProductClick={id => () => navigate(productUrl(id))}
                    onShippingAddressEdit={() =>
                      openModal("edit-shipping-address")
                    }
                    onPaymentPaid={() => openModal("mark-paid")}
                    onProfileView={() => navigate(customerUrl(order.user.id))}
                    onInvoiceClick={id =>
                      window.open(
                        order.invoices.find(invoice => invoice.id === id)?.url,
                        "_blank"
                      )
                    }
                    onInvoiceSend={id => openModal("invoice-send", { id })}
                    assignRiderDialog={() =>
                      openModal("onClickAssignRider", {
                        orderId: id
                      })
                    }
                    onSubmitMarkAsValid={onSubmitMarkAsValid}
                    onRowClick={getID => () => {
                      const temp_object = JSON.parse(getID);

                      openModal("onClickOrderProduct", {
                        productId: {
                          storeOrderId: temp_object.storeOrderId,
                          variantId: temp_object.variantId
                        }
                      });
                    }}
                    onNextPage={() => undefined}
                    onPreviousPage={() => undefined}
                    generateShippingDialog={() =>
                      openModal("onClickGenerateShippingLabel")
                    }
                    dropOffList={DropOffList?.dropOffLocations?.edges.map(
                      e => e?.node
                    )}
                    onAssignHomeLocation={id =>
                      openModal("onAssignHomeLocation", { id })
                    }
                    refetchOrder={refetch}
                    enableManageMallActions={enableManageMallActions}
                  />
                  <OrderMarkAsCompletedDialog
                    open={params.action === "onClickCompleted"}
                    onClose={closeModal}
                    handleSubmit={onCompletedConfirm}
                    order={order}
                  />
                  <OrderConfirmDialog
                    allReceivedAtFc={allReceivedAtFc}
                    allValidForPacking={allValidForPacking}
                    open={params.action === "onClickSaveButton"}
                    onClose={closeModal}
                    motherOrderStatus={maybe(
                      () => params.motherOrderStatus,
                      SmopOrderStatusEnum.RECEIVED_AT_FC
                    )}
                    order={order}
                    handleSubmit={onSubmitStatus}
                    type={"ORDER_DETAILS"}
                    updateStatusMutation={updateStatusMutation}
                  ></OrderConfirmDialog>
                  <Form
                    onSubmit={onSubmitCancelOrder}
                    initial={{
                      cancelInput: "",
                      cancelReason: "",
                      cancelType: ""
                    }}
                  >
                    {({ change, data }) => (
                      <OrderCancelDialog
                        open={params.action === "cancel"}
                        onClose={closeModal}
                        handleSubmit={onSubmitCancelOrder}
                        onChange={change}
                        data={data}
                        name="cancelType"
                      />
                    )}
                  </Form>
                  <OrderAssignRiderDialog
                    dropOffLocations={DropOffList?.dropOffLocations?.edges.map(
                      e => e?.node
                    )}
                    open={params.action === "onClickAssignRider"}
                    riderList={maybe(() =>
                      resultRider.data.search.edges
                        .map(edge => edge.node)
                        .filter(suggestedRider => suggestedRider.id)
                    )}
                    rider={data}
                    onClose={closeModal}
                    fetchRider={searchRider}
                    fetchMoreRider={{
                      hasMore: maybe(
                        () => resultRider.data.search.pageInfo.hasNextPage
                      ),
                      loading: resultRider.loading,
                      onFetchMore: loadMoreRider
                    }}
                    loading={resultRider.loading}
                    onSubmit={riderVariable => {
                      assignRider({
                        variables: {
                          id: order.id,
                          ...riderVariable
                        }
                      });
                    }}
                    on3plBooking={riderVariable => {
                      bookThirdPartyRider({
                        variables: {
                          id: order.id,
                          ...riderVariable
                        }
                      });
                    }}
                    onManualRebooking={riderVariable =>
                      manualRebookThirdPartyRider({
                        variables: {
                          id: params.orderId,
                          input: {
                            packageHeight: riderVariable.height,
                            packageLength: riderVariable.length,
                            packageWeight: riderVariable.weight,
                            packageWidth: riderVariable.width
                          }
                        }
                      })
                    }
                    onGrabExpressCancel={() => {
                      orderCancelGrabExpress({
                        variables: {
                          id: params.orderId
                        }
                      });
                    }}
                    onPandagoAutoCancel={(cancelReason: string) => {
                      orderCancelPandagoAuto({
                        variables: {
                          cancelReason,
                          id: params.orderId
                        }
                      });
                    }}
                    serverError={serverError}
                    setServerError={setServerError}
                    thirdPartyBookingStatus={bookingResult.loading}
                  />
                  <OrderProductDetailsDialog
                    open={params.action === "onClickOrderProduct"}
                    onClose={closeModal}
                    orderId={maybe(() => params.productId.variantId, "")}
                    variant={maybe(
                      () =>
                        order.subOrders.find(subOrder =>
                          subOrder.lines.some(
                            line => line.id === params.productId.variantId
                          )
                        ),
                      null
                    )}
                    // variant={maybe(
                    //   () => order.lines.find(({ id }) => params.productId === id),
                    //   pickItems
                    // )}
                  />
                  <OrderGenerateShippingLabel
                    open={params.action === "onClickGenerateShippingLabel"}
                    onUpdateShippingLabel={onUpdateShippingLabel}
                    onClose={closeModal}
                    type="ORDER_VIEW_ACTION"
                    orderData={maybe(() => order)}
                  />
                  <ActionDialog
                    open={params.action === "onAssignHomeLocation"}
                    onClose={closeModal}
                    onConfirm={() =>
                      assignHomeLocation({
                        variables: {
                          dropOffLocation: params.id,
                          id
                        }
                      })
                    }
                    confirmButtonState={assignHomeLocationOpts?.status}
                  >
                    <DialogContentText>
                      <FormattedMessage
                        defaultMessage="Are you sure you want to assign {homeLocation} to Order {orderCode}"
                        description="assign home location"
                        values={{
                          homeLocation: (
                            <b>
                              {
                                DropOffList?.dropOffLocations?.edges?.find(
                                  e => e?.node?.id === params?.id
                                )?.node?.name
                              }
                            </b>
                          ),
                          orderCode: <b>{order?.orderCode}</b>
                        }}
                      />
                    </DialogContentText>
                  </ActionDialog>
                </>
              );
            }}
          </TypedOrderCancelMutation>
        );
      }}
    </TypedOrderDetailsQuery>
  );
};

export default OrderDetails;
